import * as React from 'react';
import cn from 'classnames';

import { useTheme } from '../../theme';
import { CheckoutOrderFragment, NASStatus } from '@websky/graphql';

import Tabs from '../../Tabs/Tabs';

import FlightInfoCardTabs from './FlightInfoCardTabs/FlightInfoCardTabs';
import FlightInfoCardHeader from './FlightInfoCardHeader/FlightInfoCardHeader';
import Sticky from './Sticky/Sticky';
import { useToggleable } from '../../hooks';
import TicketsModal from './TicketsModal/TicketsModal';

interface Props {
	segmentId: string;
	setSegmentId: (id: number) => void;
	order: CheckoutOrderFragment;
	className?: string;
	fareDetailsRender?: () => React.ReactNode;
	onPassengerDetails: () => void;
	onControlTicket: () => void;
	onDownloadTickets: () => void;
}

const FlightInfoCard: React.FC<Props> = ({
	segmentId,
	setSegmentId,
	order,
	onPassengerDetails,
	onDownloadTickets,
	className
}) => {
	const { FlightInfoCard: css } = useTheme('FlightInfoCard');

	const { isOpen: isDownloadOpen, open: downloadOpen, close: downloadClose } = useToggleable(false);

	const tabs = order.flight.segments.map(segment => {
		return {
			header: `${segment.segment.departure.airport.city.name} — ${segment.segment.arrival.airport.city.name}`,
			value: +segment.segment.id
		};
	});

	const everyPortPassengerIds = React.useMemo(() => {
		const passengerIds: string[] = [];

		order?.additionalServices?.nas?.selectedServices?.forEach(service => {
			if (service.status === NASStatus.Confirmed) {
				service.travellers.forEach(traveller => {
					passengerIds.push(traveller.id);
				});
			}
		});

		return passengerIds;
	}, [order]);

	return (
		<div className={cn(css.wrapper, className)}>
			<FlightInfoCardHeader
				order={order}
				onPassengerDetails={onPassengerDetails}
				onDownloadTickets={downloadOpen}
			/>

			<Sticky stickyClassName={css.stickyTabs}>
				<Tabs
					tabs={tabs}
					selected={+segmentId}
					onChange={setSegmentId}
					selectedTabClassName={css.tabs__selectedTab}
					className={css.tabs__container}
					tabClassName={css.tabs__tab}
					tabLabelClassName={css.tabs__tab__label}
					tabHeaderClassName={css.tabs__tab__header}
					flexContainerClassName={css.flexTabsContainer}
					variant="scrollable"
				/>
			</Sticky>

			<FlightInfoCardTabs
				flight={order.flight}
				segmentId={segmentId}
				onChangeIndex={setSegmentId}
				orderId={order.id}
			/>

			<TicketsModal
				open={isDownloadOpen}
				onClose={downloadClose}
				everyPortPassengerIds={everyPortPassengerIds}
				withEveryTravelServices={!!order.price?.everyTravel}
			/>
		</div>
	);
};

export default FlightInfoCard;
